<template>
  <div class="position-relative">
    <vue-plotly
      :data="chartData"
      :layout="computedLayout"
      :options="chartOptions"
      :config="{ responsive: true }"
      @hover="hoveredData"
    />
  </div>
</template>

<script>
import VuePlotly from "@statnett/vue-plotly";
import unitConverter from "@/utils/unitConverter";
import { mapGetters } from "vuex";
import { processChartData } from "@/utils/chartDataProcessor";

export default {
  props: [
    "selectedSensors",
    "location",
    "selectedAlerts",
    "dateInterval",
  ],
  components: {
    VuePlotly,
  },
  data: () => ({
    chartLayout: {},
    chartOptions: {},
    windowWidth: window.innerWidth,
    resizeKey: 0,
  }),
  created() {
    this.chartLayout = {
      autosize: true,
      showlegend: false,
      hovermode: "x unified",
      margin: {
        l: 70,
        r: 50,
        b: 50,
        t: 50,
        pad: 4,
      },
      annotations: [],
      height: null,
      width: null,
    };
  },
  mounted() {
    window.addEventListener("resize", this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener("resize", this.handleResize);
  },

  computed: {
    ...mapGetters(["getMe"]),

    computedLayout() {
      return {
        ...this.chartLayout,
        annotations: this.chartAnnotations,
        _resizeKey: this.windowWidth,
      };
    },

    chartAnnotations() {
      return this.selectedAlerts
        .filter((item) => {
          return (
            this.$moment(this.$route.query.datefrom).unix() < item.created &&
            this.$moment(this.$route.query.dateto).unix() > item.created
          );
        })
        .map((item) => {
          const doesAnnotationExists = this.chartLayout.annotations.findIndex(
            (annotations) =>
              annotations.uuid === item.device_sensor.uuid &&
              annotations.created === item.created,
          );

          if (
            typeof doesAnnotationExists !== "undefined" &&
            doesAnnotationExists !== -1
          ) {
            return item;
          }

          const getDataObj = this.chartData.find(
            (el) => el.name === item.device_sensor.name,
          );

          if (typeof getDataObj !== "undefined" && getDataObj.x.length !== 0) {
            const nearestTime = getDataObj.x.reduce((prev, curr) => {
              return Math.abs(this.$moment(curr).unix() - item.created) <=
                Math.abs(this.$moment(prev).unix() - item.created)
                ? curr
                : prev;
            });

            const nearestTimeIndex = getDataObj.x.findIndex(
              (el) => el === nearestTime,
            );

            return {
              x: this.$moment(item.created * 1000).format(),
              y: getDataObj.y[nearestTimeIndex],
              xref: "x",
              yref: "y",
              text: item.device_sensor.name + " alert",
              showarrow: true,
              arrowhead: 7,
              uuid: item.device_sensor.uuid,
              created: item.created,
              ax: 0,
              ay: -40,
            };
          }
        });
    },

    chartData() {
      if (
        !this.dateInterval ||
        this.dateInterval.length !== 2 ||
        !this.dateInterval[0] ||
        !this.dateInterval[1]
      ) {
        return [
          {
            type: "scatter",
            mode: "lines",
            connectgaps: false,
            name: "",
            uuid: "",
            x: [],
            y: [],
          },
        ];
      }

      const unitPreference = this.getMe.user.profile.unit_preference;

      // Create format function wrapper for unit conversion
      const formatValueFn = (value, measureType) => {
        return unitConverter.format(
          value,
          measureType,
          unitPreference,
          null,
          null,
          true,
        );
      };

      return processChartData({
        sensorsData: this.selectedSensors.data,
        locationData: this.location,
        dateInterval: this.dateInterval,
        formatValueFn: formatValueFn,
        maxGapHours: 1,
      });
    },
  },
  methods: {
    handleResize() {
      this.windowWidth = window.innerWidth;
    },
    hoveredData(val) {
      if (val && val.points && val.points.length > 0 && val.points[0].x) {
        this.$emit("set-hovered-data", this.$moment(val.points[0].x).unix());
      }
    },
  },
};
</script>
<style>
/* This targets the hover tooltip group ONLY if it contains the specific "HIDE_LOCATION" identifier.
   It prevents the black arrow and label from appearing without hiding the X-axis timestamp box.
   We use attribute selectors ([data-unformatted]) so that other sensor hovers stay fully visible. */
.plotly .hoverlayer .hovertext:has(text[data-unformatted="HIDE_LOCATION"]) {
  display: none !important;
}

/* This fallback ensures that even if the container isn't hidden, the internal text and pointer are.
   It specifically wipes out the "0" value and the pointer path associated with the dummy trace.
   This maintains a clean 'X unified' look by surgically removing the invisible trace's artifacts. */
.plotly .hoverlayer .hovertext text[data-unformatted="HIDE_LOCATION"],
.plotly .hoverlayer .hovertext text[data-unformatted="HIDE_LOCATION"] ~ text,
.plotly .hoverlayer .hovertext text[data-unformatted="HIDE_LOCATION"] ~ path,
.plotly .hoverlayer .hovertext text[data-unformatted="HIDE_LOCATION"] ~ rect {
  display: none !important;
}
</style>
